import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  BehaviorSubject,
  filter,
  first,
  Observable,
  switchMap,
  throwError,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../services/auth/auth.service';
import { StorageService } from '../services/storage/storage.service';
import { skipToastErrors } from '../consts/skipToastErrors.const';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environment';
import { WebViewService } from '../services/webview/webview.service';

export const isApiError = (
  error: unknown
): error is {
  messages: [string];
} => {
  return (
    !!error &&
    typeof error === 'object' &&
    'messages' in error &&
    Array.isArray(error.messages) &&
    error.messages.length > 0 &&
    typeof error.messages[0] === 'string'
  );
};

export const getErrorMessage = (apiErrorResponse: unknown): string => {
  return isApiError(apiErrorResponse) ? apiErrorResponse.messages[0] || '' : '';
};

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken(() => false);

export const ERROR_MESSAGE = new HttpContextToken<string | null>(() => null);

export const HTTP_STATUS_CODES = {
  Client: {
    ClientErrorsStatus: 400,
    BadRequest: 400,
    Unauthorized: 401,
    Forbidden: 403,
    NotFound: 404,
  },
  Server: {
    ServerErrorsStatus: 500,
    InternalServerError: 500,
    BadGateway: 502,
    ServiceUnavailable: 503,
  },
} as const;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  private readonly refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private toastrService: ToastrService,
    private authService: AuthService,
    private storageService: StorageService,
    private readonly translateService: TranslateService,
    private readonly dialogRef: MatDialog,
    private readonly webViewService: WebViewService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        const apiErrorResponse = err.error as unknown;

        const errorMessage = getErrorMessage(apiErrorResponse);

        if (
          err.status === HTTP_STATUS_CODES.Client.Unauthorized &&
          !localStorage.getItem('mobile_app') &&
          this.storageService.getToken()
        ) {
          return this.handle401Error(request, next, err);
        }

        const shouldNotSkipToast =
          !Object.values(skipToastErrors).includes(errorMessage) &&
          !request.context.get(SKIP_ERROR_INTERCEPTOR);

        if (
          err.status === HTTP_STATUS_CODES.Client.BadRequest &&
          shouldNotSkipToast
        ) {
          this.showErrorMessage(errorMessage);
        }

        if (err.status === HTTP_STATUS_CODES.Client.Forbidden) {
          this.showErrorMessage(
            'У вас нет доступа для просмотра этого контента!',
            'Ошибка доступа!'
          );
        }

        if (
          err.status === HTTP_STATUS_CODES.Server.BadGateway &&
          request.url.includes(environment.converterBaseUrl) === false
        ) {
          this.authService.navigateToErrorPage();
          return throwError(() => err);
        }

        if (err.status >= HTTP_STATUS_CODES.Server.ServerErrorsStatus) {
          this.toastrService.clear();
          this.showErrorMessage(
            request.context.get(ERROR_MESSAGE) ?? errorMessage
          );
        }

        if (
          err.status >= HTTP_STATUS_CODES.Client.NotFound &&
          errorMessage &&
          err.status < HTTP_STATUS_CODES.Server.ServerErrorsStatus
        ) {
          this.showErrorMessage(
            request.context.get(ERROR_MESSAGE) ?? errorMessage
          );
        }

        return throwError(() => err);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse
  ): Observable<any> {
    if (request.url.includes('refresh')) {
      this.logout();
      return throwError(() => err);
    }

    if (!this.isRefreshing) {
      this.refreshTokenSubject.next(null);
      this.isRefreshing = true;

      return this.authService
        .refreshToken(this.storageService.getRefreshToken())
        .pipe(
          catchError(error => {
            this.logout();
            return throwError(() => error);
          }),
          switchMap(res => {
            if (res && res.token) {
              this.storageService.setToken(res.token);
              this.storageService.setRefreshToken(res.refreshToken);
              this.refreshTokenSubject.next(res.token);
              this.isRefreshing = false;

              return next.handle(this.addToken(request, res.token));
            } else {
              this.logout();
              return throwError(() => err);
            }
          })
        );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(Boolean),
        first(),
        switchMap(token => next.handle(this.addToken(request, token!)))
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private logout(): void {
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
    this.dialogRef.closeAll();
    this.authService.logout();
    this.webViewService.onLogOut(true);
  }

  private showErrorMessage(message: string, toastTitle?: string): void {
    this.toastrService.error(
      message,
      toastTitle ?? this.translateService.instant('error')
    );
  }
}
